import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RoutesRecognized } from '@angular/router';
import { filter, firstValueFrom, of, pairwise, Subject, Subscription, tap } from 'rxjs';

import { N4S_SHIPMENT_SELECTION_ALLOWED_MILLS } from '../../config';
import {
	SnackbarSubmissionFeedbackComponent
} from '../../shared/components/snackbar-submission-feedback/snackbar-submission-feedback.component';
import { SoldToRelationshipGroup } from '../../shared/models/service';
import { ErrorService } from '../../shared/services/error.service';
import { LocationService } from '../../shared/services/location.service';
import { TranslationService } from '../../shared/services/translation.service';
import { UserService } from '../../shared/services/user.service';
import { createDefaultMillOffer } from '../models/mill-offer';
import { ExchangeRateLastResponse, ExchangeRateValue, Need4SteelOffer } from '../models/need4steel';
import { Need4SteelWizardState, Need4SteelWizardStep } from '../models/need4steel-wizard-state';
import { formatNumber } from '../../shared/utils/number.format';

import { MillOfferService } from './mill-offer.service';
import { Need4SteelService } from './need4steel.api.service';
import { N4sShipmentCacheService } from './n4s-shipment-cache.service';

export const PAGE_NAME_STEPS = ['productSpecification', 'summary'];
export const N4S_SERVICE = 'ND4ST';
const INVOICING_CURRENCY_PARAM = 'INVOICING_CURRENCY';

@Injectable({ providedIn: 'root' })
export class Need4SteelWizardService {
	previousUrl: string | undefined;
	urlData: string[] = [];
	userBocData?: SoldToRelationshipGroup[];
	exchangeRateResponse?: ExchangeRateLastResponse;
	backViewSubscription?: Subscription;
	private exchangeRateHighlighted = false;
	private exchangeRateHighlightTimeout?: number;
	private readonly triggerScrollSubject = new Subject<number>();
	private state: Need4SteelWizardState;
	private readonly soldToChangesubject = new Subject<string>();
	private readonly endUserChangeSubject = new Subject<void>();
	constructor(
		private readonly millOfferService: MillOfferService,
		private readonly errorService: ErrorService,
		private readonly router: Router,
		private readonly locationService: LocationService,
		private readonly location: Location,
		private readonly need4SteelService: Need4SteelService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly shipmentCacheService: N4sShipmentCacheService,
		private readonly snackBar: MatSnackBar,
	) {
		const defaultMillOffer = createDefaultMillOffer();
		this.state = new Need4SteelWizardState(defaultMillOffer, defaultMillOffer);
	}

	getState() {
		return this.state;
	}

	getTriggerScrollObservable() {
		return this.triggerScrollSubject.asObservable();
	}

	getStateOfferForm() {
		return this.state.offer.form;
	}

	async actionNewOffer() {
		try {
			const defaultMillOffer = createDefaultMillOffer();
			this.initMillOffer(defaultMillOffer, defaultMillOffer);
			return await this.router.navigateByUrl('need4steel/wizard');
		} catch (err) {
			console.error('unable to create new mill offer', err);
			this.millOfferService.showError();
			return false;
		}
	}

	scrollToItemRow(index: number): void {
		this.triggerScrollSubject.next(index);
	}

	actionResetCurrentOffer() {
		this.state.offer.reset();
	}

	async actionGoNextStep() {
		if (this.state.stepIndex >= Need4SteelWizardStep.CONFIRMATION) {
			return;
		}
		const maxStepIndex = this.state.offer.computeIndexStep();
		if (this.state.stepIndex > maxStepIndex) {
			return;
		}
		// push navigation step
		this.state.stepIndex += 1;

		const url = `need4steel/wizard/${ PAGE_NAME_STEPS[this.state.stepIndex] }`;
		if (this.state.stepIndex !== Need4SteelWizardStep.CONFIRMATION) {
			await this.router.navigateByUrl(url);
		} else {
			this.locationService.go(-this.state.stepIndex, url);
		}
	}

	actionGoPreviousStep() {
		if (this.state.stepIndex <= Need4SteelWizardStep.HEADER) {
			return;
		}
		this.state.stepIndex -= 1;
		this.location.back();
	}

	actionToGoBack() {
		this.backViewSubscription = this.router.events
			.pipe(
				filter((ev): ev is RoutesRecognized => ev instanceof RoutesRecognized),
				pairwise()
			)
			.subscribe(ev => {
				for (const url of ev) {
					this.urlData.push(url.urlAfterRedirects);
				}
			});
		const disconIdx = this.urlData.indexOf('/need4steel/wizard/stepHeader');
		return this.urlData[disconIdx - 1];
	}

	actionCleanUrlData() {
		this.urlData = [];
	}

	actionSavePreviousPage(previousPage: string | undefined) {
		this.previousUrl = previousPage;
	}

	actionRetrievePreviousPage() {
		return this.previousUrl;
	}

	async actionMoveUsingSteps(step: Need4SteelWizardStep) {
		if (this.state.stepIndex <= Need4SteelWizardStep.HEADER) {
			return;
		}
		if (step < this.state.stepIndex) {
			this.state.stepIndex = 0;
			const url = `need4steel/wizard/${ PAGE_NAME_STEPS[step] }`;
			await this.router.navigateByUrl(url);
		}
	}

	getCurrentRoute(): string {
		return this.router.url;
	}

	actionClearWizardHistory() {
		this.state.isValid = false;
		this.state.exiting = false;
		this.exchangeRateResponse = undefined;
		const defaultMillOffer = createDefaultMillOffer();
		this.initMillOffer(defaultMillOffer, defaultMillOffer);
	}

	soldToChangeEvent(soldto: string) {
		this.soldToChangesubject.next(soldto);
	}

	onsoldToChangeEventSubscribe() {
		return this.soldToChangesubject.asObservable();
	}

	endUserChangeEvent() {
		this.endUserChangeSubject.next();
	}

	onEndUserChangeEventSubscribe() {
		return this.endUserChangeSubject.asObservable();
	}

	async actionSubmitOrder() {
		try {
			const requestObj = this.millOfferService.requestOrderObj;
			await firstValueFrom(this.need4SteelService.submitOrder(requestObj));
			this.millOfferService.resetMillOfferDetails();
			await this.router.navigateByUrl('/home');
			this.snackBar.openFromComponent(
				SnackbarSubmissionFeedbackComponent,
				{
					data: { hideResponseDetails: true,
						isThanksMessage: true,
						isRedirectLink: false,
						submitThanksMessage: this.translationService.getTR('confirm.thanks') },
					duration: 20_000,
					panelClass: ['copo-snackbar-submission-feedback-container']
				}
			);
			return true;
		} catch (err) {
			if (!(err instanceof HttpErrorResponse)) {
				return false;
			}
			if (typeof err.error === 'object' && err.error !== null) {
				if ((<{ code: string }> err.error).code === 'CREDITLIMIT_CHECK_FAILED') {
					this.errorService.setErrorMsg(
						this.translationService.getTR('need4steel.submit.credit.limit.error.msg'),
						'need4steel.submit.order.error.msg.title'
					);
					return false;
				} else if ((<{ code: string }> err.error).code === 'EXCHANGE_RATE_MANDATORY') {
					this.errorService.setErrorMsg(
						this.translationService.getTR('need4steel.submit.exchange.rate.mandatory.error.msg'),
						'need4steel.submit.order.error.msg.title'
					);
					return false;
				} else if ((<{ code: string }> err.error).code === 'EXCHANGE_RATE_REFRESHED') {
					try {
						await firstValueFrom(this.refreshExchangeRates());
						this.triggerExchangeRateHighlight();
					} catch (refreshError) {
						console.error('error refreshing exchange rate after submit rejection', refreshError);
					}
					this.errorService.setErrorMsg(
						this.translationService.getTR('need4steel.submit.exchange.rate.refreshed.error.msg'),
						'need4steel.submit.order.error.msg.title'
					);
					return false;
				} else if ((<{ message: string }> err.error).message) {
					this.errorService.setErrorMsg(
						(<{ message: string }> err.error).message,
						'need4steel.submit.order.error.msg.title'
					);
					return false;
				}
			}
			this.errorService.setErrorMsg(
				this.translationService.getTR('need4steel.submit.error.msg'),
				'need4steel.submit.order.error.msg.title'
			);
			console.error(err);
			return false;
		}
	}

	actionExitWizard() {
		this.millOfferService.resetMillOfferDetails();
		this.actionClearWizardHistory();
		return true;
	}

	isMillOfferIdPresent() {
		return this.millOfferService.getMillOfferId() !== null;
	}

	updateStepIndex(stepIndex: number) {
		this.state.stepIndex = stepIndex;
	}

	getInvoicingCurrency() {
		const paramValue = this.userService.user.getServiceParameterValue(
			N4S_SERVICE,
			INVOICING_CURRENCY_PARAM
		);
		// Extract currency code from parameter value (e.g., 'CURRENCY_GBP' -> 'GBP')
		if (paramValue && paramValue !== 'CURRENCY_NONE') {
			return paramValue.replace('CURRENCY_', '');
		}
		return undefined;
	}

	isShipmentSelectionEnabled(millSite: string | undefined): boolean {
		const millsConfig = <string | undefined> N4S_SHIPMENT_SELECTION_ALLOWED_MILLS;
		if (!millSite || !millsConfig) {
			return false;
		}
		const allowedMills = millsConfig
			.split(',')
			.map(m => m.trim())
			.filter(m => m.length > 0 && m.toLowerCase() !== 'none');
		return allowedMills.includes(millSite);
	}

	shouldDisplayExchangeRate() {
		return this.getInvoicingCurrency() !== undefined;
	}

	loadExchangeRates() {
		if (this.exchangeRateResponse) {
			return of(this.exchangeRateResponse);
		}
		return this.need4SteelService.getLastExchangeRates().pipe(tap(response => {
			this.exchangeRateResponse = response;
		}));
	}

	refreshExchangeRates() {
		this.exchangeRateResponse = undefined;
		return this.loadExchangeRates();
	}

	getExchangeRate(currencyCode: string): ExchangeRateValue | undefined {
		const upperCurrency = currencyCode.toUpperCase();
		return this.exchangeRateResponse?.rates.find(rate => rate.currency.toUpperCase() === upperCurrency);
	}

	getExchangeRateReferenceDate() {
		return this.exchangeRateResponse?.referenceDate;
	}

	hasExchangeRateToDisplay() {
		const currency = this.getInvoicingCurrency();
		return Boolean(currency && this.getExchangeRate(currency));
	}

	getExchangeRateDisplayCurrency() {
		return this.getInvoicingCurrency() || '';
	}

	getExchangeRateDisplayValue() {
		const currency = this.getInvoicingCurrency();
		if (!currency) {
			return '';
		}
		const exchangeRate = this.getExchangeRate(currency);
		return exchangeRate ? formatNumber(exchangeRate.value, 'amount') : '';
	}

	getExchangeRateDisplayReferenceDate() {
		return this.getExchangeRateReferenceDate() || '';
	}

	shouldHighlightExchangeRate() {
		return this.exchangeRateHighlighted;
	}

	triggerExchangeRateHighlight() {
		this.exchangeRateHighlighted = false;
		if (this.exchangeRateHighlightTimeout !== undefined) {
			globalThis.clearTimeout(this.exchangeRateHighlightTimeout);
		}
		globalThis.setTimeout(() => {
			this.exchangeRateHighlighted = true;
			this.exchangeRateHighlightTimeout = globalThis.setTimeout(() => {
				this.exchangeRateHighlighted = false;
				this.exchangeRateHighlightTimeout = undefined;
			}, 2000);
		});
	}

	private initMillOffer(offer: Need4SteelOffer, lastSavedOffer: Need4SteelOffer) {
		this.shipmentCacheService.resetCache();
		this.state = new Need4SteelWizardState(offer, lastSavedOffer);
		this.state.isValid = true;
	}
}
